<template>
  <!-- Miniatura: la imagen entera es el botón que abre el visor. -->
  <button
    ref="disparador"
    type="button"
    v-bind="$attrs"
    class="imagen-ampliable relative block cursor-zoom-in overflow-hidden rounded-gob-md"
    :aria-label="`Ver en pantalla completa: ${nombreAccesible}`"
    aria-haspopup="dialog"
    @click="abrir"
  >
    <video
      v-if="esVideo"
      :src="src"
      class="imagen-ampliable-medio"
      :class="claseImagen"
      :style="{ aspectRatio: relacion }"
      :width="ancho"
      :height="alto"
      :autoplay="autorreproducir"
      :loop="autorreproducir"
      muted
      playsinline
      preload="metadata"
    ></video>
    <img
      v-else
      :src="src"
      alt=""
      class="imagen-ampliable-medio"
      :class="claseImagen"
      :style="{ aspectRatio: relacion }"
      :width="ancho"
      :height="alto"
      loading="lazy"
      decoding="async"
    />
    <span class="imagen-ampliable-icono" aria-hidden="true">
      <IconArrowsMaximize />
    </span>
  </button>

  <!-- En <body>: así no lo recortan ni lo afectan el sticky, el inert o el overflow de quien lo usa. -->
  <Teleport to="body">
    <dialog ref="dialogo" class="visor" :aria-label="nombreAccesible" @close="alCerrar" @click="alClicFondo">
      <button type="button" class="visor-cerrar" aria-label="Cerrar pantalla completa" @click="cerrar">
        <IconX aria-hidden="true" />
      </button>
      <div class="visor-marco">
        <video
          v-if="abierto && esVideo"
          :src="src"
          class="visor-imagen"
          :aria-label="nombreAccesible"
          controls
          :autoplay="autorreproducir"
          :loop="autorreproducir"
          muted
          playsinline
        ></video>
        <img v-else-if="abierto" :src="src" :alt="nombreAccesible" class="visor-imagen" />
      </div>
    </dialog>
  </Teleport>
</template>

<script>
import { IconArrowsMaximize, IconX } from '@tabler/icons-vue'

/**
 * GobImagenAmpliable
 * Imagen o video que se abre a pantalla completa (según la extensión de `src`: .mp4/.webm se
 * reproducen con <video>, el resto se muestra con <img>). La miniatura es un botón; el visor es un
 * <dialog> nativo en modo modal (foco atrapado, Esc cierra, el resto de la página queda inerte) con
 * el medio completo, sin recorte. Cierra con el botón, con Esc o tocando fuera del medio; bloquea el
 * scroll mientras está abierto y devuelve el foco a la miniatura sin desplazar la página.
 * Las clases y atributos del padre van al botón (`inheritAttrs: false`, el componente tiene dos raíces);
 * las del medio, en `claseImagen`.
 *
 * Los video reemplazan a los antiguos GIF de producto (mismo peso visual, una fracción del peso en
 * bytes): silenciosos y en bucle automático en la miniatura, con controles en el visor a pantalla
 * completa para que la persona pueda pausarlos.
 *
 * La miniatura ocupa siempre la misma caja (`relacion`, 16:9 por defecto) reciba el medio que reciba:
 * uno cuadrado o muy alto se reduce hasta caber entero y se centra, con el sobrante como fondo
 * neutro. `claseImagen` es para borde, radio y color; no debe traer `aspect-*` ni `object-cover`,
 * que es justo lo que antes recortaba los GIF de producto por abajo.
 */
export default {
  name: 'GobImagenAmpliable',
  components: { IconArrowsMaximize, IconX },
  inheritAttrs: false,
  props: {
    src: { type: String, required: true },
    /** Texto alternativo. Si llega vacío, se usa «Captura de {titulo}». */
    alt: { type: String, default: '' },
    /** Nombre de lo que muestra la imagen (módulo o solución), respaldo del nombre accesible. */
    titulo: { type: String, required: true },
    claseImagen: { type: [String, Array, Object], default: '' },
    /**
     * Proporción de la caja de la miniatura, en la misma forma que `GobMedia` («16 / 9»). Fija el
     * tamaño que ocupa la miniatura sea cual sea la proporción real del medio: lo que no encaja
     * se resuelve reduciéndolo, nunca recortándolo.
     */
    relacion: { type: String, default: '16 / 9' },
    ancho: { type: [Number, String], default: 1280 },
    alto: { type: [Number, String], default: 720 }
  },
  data() {
    return {
      /** La imagen grande solo se monta con el visor abierto. */
      abierto: false,
      /** Los video de producto no autorreproducen ni se repiten si la persona prefiere menos movimiento. */
      reducirMovimiento: window.matchMedia('(prefers-reduced-motion: reduce)').matches
    }
  },
  computed: {
    nombreAccesible() {
      return this.alt || `Captura de ${this.titulo}`
    },
    esVideo() {
      return /\.(mp4|webm)$/i.test(this.src)
    },
    autorreproducir() {
      return !this.reducirMovimiento
    }
  },
  beforeUnmount() {
    // Si se sale de la ruta con el visor abierto, no dejar la página sin scroll.
    if (this.abierto) {
      document.documentElement.style.overflow = ''
    }
  },
  methods: {
    abrir() {
      this.abierto = true
      document.documentElement.style.overflow = 'hidden'
      this.$refs.dialogo.showModal()
    },
    cerrar() {
      this.$refs.dialogo.close()
    },
    /** Se dispara al cerrar por cualquier vía (botón, Esc o fondo). */
    alCerrar() {
      this.abierto = false
      document.documentElement.style.overflow = ''
      this.$refs.disparador.focus({ preventScroll: true })
    },
    /** Cerrar al tocar fuera de la imagen: el propio <dialog> o el marco que la centra. */
    alClicFondo(evento) {
      if (evento.target === this.$refs.dialogo || evento.target.classList.contains('visor-marco')) {
        this.cerrar()
      }
    }
  }
}
</script>

<style scoped>
/*
 * Caja de proporción estable. Antes la miniatura heredaba `object-cover` de quien la usaba y, con
 * cualquier medio que no fuera 16:9 —que son casi todos los GIF de producto—, le recortaba la
 * parte inferior; además su alto cambiaba según la imagen recibida. Ahora la caja fija el tamaño,
 * el medio se reduce hasta caber entero y queda centrado, y el sobrante se lee como fondo neutro
 * en vez de como un corte.
 */
.imagen-ampliable-medio {
  display: block;
  width: 100%;
  height: auto;
  object-fit: contain;
  object-position: center;
  background-color: var(--color-background-alt);
}

.imagen-ampliable img,
.imagen-ampliable video {
  transition: transform var(--duration-gob) var(--ease-gob-out);
}

.imagen-ampliable-icono {
  position: absolute;
  top: 0.75rem;
  right: 0.75rem;
  display: grid;
  width: 2.5rem;
  height: 2.5rem;
  place-items: center;
  border-radius: 9999px;
  background-color: rgb(10 12 64 / 0.8);
  color: #FFFFFF;
  opacity: 0;
  transform: scale(0.9);
  transition:
    opacity var(--duration-gob) var(--ease-gob-out),
    transform var(--duration-gob) var(--ease-gob-out);
}

.imagen-ampliable-icono :deep(svg) {
  width: 1.25rem;
  height: 1.25rem;
}

.imagen-ampliable:hover img,
.imagen-ampliable:hover video {
  transform: scale(1.02);
}

.imagen-ampliable:hover .imagen-ampliable-icono,
.imagen-ampliable:focus-visible .imagen-ampliable-icono {
  opacity: 1;
  transform: none;
}

/* Sin cursor no hay hover: el ícono queda visible para anunciar que la imagen se amplía. */
@media (hover: none) {
  .imagen-ampliable-icono {
    opacity: 1;
    transform: none;
  }
}

.visor {
  --color-focus-ring: #FFFFFF;
  position: fixed;
  inset: 0;
  width: 100%;
  max-width: none;
  height: 100%;
  max-height: none;
  margin: 0;
  padding: clamp(1rem, 4vw, 3rem);
  border: 0;
  background-color: rgb(10 12 64 / 0.94);
  color: #FFFFFF;
  overflow: hidden;
}

/*
 * Las pistas van declaradas, no implícitas. Con `display: grid` a secas la fila era `auto` y crecía
 * hasta el alto intrínseco del medio; entonces el `height: 100%` del marco se medía contra esa fila
 * ya crecida, y el `max-height: 100%` de la imagen contra el marco crecido, así que no limitaba
 * nada y el `overflow: hidden` de arriba cortaba el sobrante por abajo. `minmax(0, 1fr)` fija la
 * pista al área disponible y le quita el mínimo automático, que es lo que permitía el crecimiento.
 */
.visor[open] {
  display: grid;
  grid-template-rows: minmax(0, 1fr);
  grid-template-columns: minmax(0, 1fr);
  animation: visor-aparecer var(--duration-gob) var(--ease-gob-out);
}

.visor::backdrop {
  background: transparent;
}

/*
 * Flex y no grid: el `max-height: 100%` de un elemento flexible se resuelve contra el alto del
 * contenedor, que aquí ya es definido. Con grid volvía a medirse contra una fila `auto` capaz de
 * crecer, que es el mismo fallo de arriba un nivel más abajo.
 */
.visor-marco {
  display: flex;
  width: 100%;
  height: 100%;
  min-height: 0;
  align-items: center;
  justify-content: center;
}

/*
 * Los `min-*` en cero anulan el mínimo automático del elemento flexible, que vale el tamaño
 * intrínseco del medio y, al ganarle en la cascada a `max-*`, dejaría desbordar un GIF más grande
 * que la pantalla. Con ellos, la imagen se reduce hasta caber entera por ambos lados.
 */
.visor-imagen {
  min-width: 0;
  min-height: 0;
  max-width: 100%;
  max-height: 100%;
  border-radius: 12px;
  object-fit: contain;
  box-shadow: 0 24px 64px rgb(0 0 0 / 0.45);
}

.visor-cerrar {
  position: absolute;
  top: 1rem;
  right: 1rem;
  z-index: 1;
  display: grid;
  width: 3rem;
  height: 3rem;
  place-items: center;
  border-radius: 9999px;
  background-color: rgb(255 255 255 / 0.14);
  color: #FFFFFF;
  cursor: pointer;
  transition: background-color var(--duration-gob) var(--ease-gob-out);
}

.visor-cerrar:hover {
  background-color: rgb(255 255 255 / 0.26);
}

.visor-cerrar :deep(svg) {
  width: 1.5rem;
  height: 1.5rem;
}

@keyframes visor-aparecer {
  from {
    opacity: 0;
  }
}

@media (prefers-reduced-motion: reduce) {
  .imagen-ampliable img,
  .imagen-ampliable video,
  .imagen-ampliable-icono {
    transition: none;
  }

  .imagen-ampliable:hover img,
  .imagen-ampliable:hover video {
    transform: none;
  }

  .visor[open] {
    animation: none;
  }
}
</style>
